/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directionality} from '@angular/cdk/bidi';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {ESCAPE} from '@angular/cdk/keycodes';
import {
  Overlay,
  OverlayRef,
  OverlayConfig,
  PositionStrategy,
  RepositionScrollStrategy,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {first} from '@angular/cdk/rxjs';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import {DateAdapter, MATERIAL_COMPATIBILITY_MODE} from '@angular/material/core';
import {MdDialog, MdDialogRef} from '@angular/material/dialog';
import {DOCUMENT} from '@angular/platform-browser';
import {Subject} from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';
import {MdCalendar} from './calendar';
import {coerceDateProperty} from './coerce-date-property';
import {createMissingDateImplError} from './datepicker-errors';
import {MdDatepickerInput} from './datepicker-input';
/**
 * Used to generate a unique ID for each datepicker instance.
 */
let datepickerUid = 0;
/**
 * Injection token that determines the scroll handling while the calendar is open.
 */
export const MD_DATEPICKER_SCROLL_STRATEGY =
    new InjectionToken<() => ScrollStrategy>('md-datepicker-scroll-strategy');
/**
 * \@docs-private
 * @param {?} overlay
 * @return {?}
 */
export function MD_DATEPICKER_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay):
    () => RepositionScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}
/**
 * \@docs-private
 */
export const MD_DATEPICKER_SCROLL_STRATEGY_PROVIDER = {
  provide: MD_DATEPICKER_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: MD_DATEPICKER_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
/**
 * Component used as the content for the datepicker dialog and popup. We use this instead of using
 * MdCalendar directly as the content so we can control the initial focus. This also gives us a
 * place to put additional features of the popup that are not part of the calendar itself in the
 * future. (e.g. confirmation buttons).
 * \@docs-private
 */
export class MdDatepickerContent<D> implements AfterContentInit {
  datepicker: MdDatepicker<D>;

   _calendar: MdCalendar<D>;
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._calendar._focusActiveCell();
  }
/**
 * Handles keydown event on datepicker content.
 * @param {?} event The event.
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ESCAPE) {
      this.datepicker.close();
      event.preventDefault();
      event.stopPropagation();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-datepicker-content, mat-datepicker-content',
  templateUrl: 'datepicker-content.html',
  styleUrls: ['datepicker-content.css'],
  host: {
    'class': 'mat-datepicker-content',
    '[class.mat-datepicker-content-touch]': 'datepicker.touchUi',
    '(keydown)': '_handleKeydown($event)',
  },
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_calendar': [{ type: ViewChild, args: [MdCalendar, ] },],
};
}

function MdDatepickerContent_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepickerContent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepickerContent.ctorParameters;
/** @type {?} */
MdDatepickerContent.propDecorators;
/** @type {?} */
MdDatepickerContent.prototype.datepicker;
/** @type {?} */
MdDatepickerContent.prototype._calendar;
}

/**
 * Component responsible for managing the datepicker popup/dialog.
 */
export class MdDatepicker<D> implements OnDestroy {
/**
 * The date to open the calendar to initially.
 * @return {?}
 */
get startAt(): D | null {
    // If an explicit startAt is set we start there, otherwise we start at whatever the currently
    // selected value is.
    return this._startAt || (this._datepickerInput ? this._datepickerInput.value : null);
  }
/**
 * @param {?} date
 * @return {?}
 */
set startAt(date: D | null) { this._startAt = coerceDateProperty(this._dateAdapter, date); }
private _startAt: D | null;
/**
 * The view that the calendar should start in.
 */
startView: 'month' | 'year' = 'month';
/**
 * Whether the calendar UI is in touch mode. In touch mode the calendar opens in a dialog rather
 * than a popup and elements have more padding to allow for bigger touch targets.
 */
touchUi = false;
/**
 * Whether the datepicker pop-up should be disabled.
 * @return {?}
 */
get disabled() {
    return this._disabled === undefined ? this._datepickerInput.disabled : this._disabled;
  }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) {
    const /** @type {?} */ newValue = coerceBooleanProperty(value);

    if (newValue !== this._disabled) {
      this._disabled = newValue;
      this._disabledChange.next(newValue);
    }
  }
private _disabled: boolean;
/**
 * Emits new selected date when selected date changes.
 * @deprecated Switch to the `dateChange` and `dateInput` binding on the input element.
 */
selectedChanged = new EventEmitter<D>();
/**
 * Whether the calendar is open.
 */
opened = false;
/**
 * The id for the datepicker calendar.
 */
id = `md-datepicker-${datepickerUid++}`;
/**
 * The currently selected date.
 * @return {?}
 */
get _selected(): D | null { return this._validSelected; }
/**
 * @param {?} value
 * @return {?}
 */
set _selected(value: D | null) { this._validSelected = value; }
private _validSelected: D | null = null;
/**
 * The minimum selectable date.
 * @return {?}
 */
get _minDate(): D | null {
    return this._datepickerInput && this._datepickerInput.min;
  }
/**
 * The maximum selectable date.
 * @return {?}
 */
get _maxDate(): D | null {
    return this._datepickerInput && this._datepickerInput.max;
  }
/**
 * @return {?}
 */
get _dateFilter(): (date: D | null) => boolean {
    return this._datepickerInput && this._datepickerInput._dateFilter;
  }
/**
 * A reference to the overlay when the calendar is opened as a popup.
 */
private _popupRef: OverlayRef;
/**
 * A reference to the dialog when the calendar is opened as a dialog.
 */
private _dialogRef: MdDialogRef<any> | null;
/**
 * A portal containing the calendar for this datepicker.
 */
private _calendarPortal: ComponentPortal<MdDatepickerContent<D>>;
/**
 * The element that was focused before the datepicker was opened.
 */
private _focusedElementBeforeOpen: HTMLElement | null = null;
private _inputSubscription = Subscription.EMPTY;
/**
 * The input element this datepicker is associated with.
 */
_datepickerInput: MdDatepickerInput<D>;
/**
 * Emits when the datepicker is disabled.
 */
_disabledChange = new Subject<boolean>();
/**
 * @param {?} _dialog
 * @param {?} _overlay
 * @param {?} _ngZone
 * @param {?} _viewContainerRef
 * @param {?} _scrollStrategy
 * @param {?} _dateAdapter
 * @param {?} _dir
 * @param {?} _document
 */
constructor(private _dialog: MdDialog,
private _overlay: Overlay,
private _ngZone: NgZone,
private _viewContainerRef: ViewContainerRef,
private _scrollStrategy,
private _dateAdapter: DateAdapter<D>,
private _dir: Directionality,
private _document: any) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.close();
    this._inputSubscription.unsubscribe();
    this._disabledChange.complete();

    if (this._popupRef) {
      this._popupRef.dispose();
    }
  }
/**
 * Selects the given date
 * @param {?} date
 * @return {?}
 */
_select(date: D): void {
    let /** @type {?} */ oldValue = this._selected;
    this._selected = date;
    if (!this._dateAdapter.sameDate(oldValue, this._selected)) {
      this.selectedChanged.emit(date);
    }
  }
/**
 * Register an input with this datepicker.
 * @param {?} input The datepicker input to register with this datepicker.
 * @return {?}
 */
_registerInput(input: MdDatepickerInput<D>): void {
    if (this._datepickerInput) {
      throw Error('An MdDatepicker can only be associated with a single input.');
    }
    this._datepickerInput = input;
    this._inputSubscription =
        this._datepickerInput._valueChange.subscribe((value: D | null) => this._selected = value);
  }
/**
 * Open the calendar.
 * @return {?}
 */
open(): void {
    if (this.opened || this.disabled) {
      return;
    }
    if (!this._datepickerInput) {
      throw Error('Attempted to open an MdDatepicker with no associated input.');
    }
    if (this._document) {
      this._focusedElementBeforeOpen = this._document.activeElement;
    }

    this.touchUi ? this._openAsDialog() : this._openAsPopup();
    this.opened = true;
  }
/**
 * Close the calendar.
 * @return {?}
 */
close(): void {
    if (!this.opened) {
      return;
    }
    if (this._popupRef && this._popupRef.hasAttached()) {
      this._popupRef.detach();
    }
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
    if (this._calendarPortal && this._calendarPortal.isAttached) {
      this._calendarPortal.detach();
    }
    if (this._focusedElementBeforeOpen &&
      typeof this._focusedElementBeforeOpen.focus === 'function') {

      this._focusedElementBeforeOpen.focus();
      this._focusedElementBeforeOpen = null;
    }

    this.opened = false;
  }
/**
 * Open the calendar as a dialog.
 * @return {?}
 */
private _openAsDialog(): void {
    this._dialogRef = this._dialog.open(MdDatepickerContent, {
      direction: this._dir ? this._dir.value : 'ltr',
      viewContainerRef: this._viewContainerRef,
    });
    this._dialogRef.afterClosed().subscribe(() => this.close());
    this._dialogRef.componentInstance.datepicker = this;
  }
/**
 * Open the calendar as a popup.
 * @return {?}
 */
private _openAsPopup(): void {
    if (!this._calendarPortal) {
      this._calendarPortal = new ComponentPortal(MdDatepickerContent, this._viewContainerRef);
    }

    if (!this._popupRef) {
      this._createPopup();
    }

    if (!this._popupRef.hasAttached()) {
      let /** @type {?} */ componentRef: ComponentRef<MdDatepickerContent<D>> =
          this._popupRef.attach(this._calendarPortal);
      componentRef.instance.datepicker = this;

      // Update the position once the calendar has rendered.
      first.call(this._ngZone.onStable.asObservable()).subscribe(() => {
        this._popupRef.updatePosition();
      });
    }

    this._popupRef.backdropClick().subscribe(() => this.close());
  }
/**
 * Create the popup.
 * @return {?}
 */
private _createPopup(): void {
    const /** @type {?} */ overlayState = new OverlayConfig({
      positionStrategy: this._createPopupPositionStrategy(),
      hasBackdrop: true,
      backdropClass: 'mat-overlay-transparent-backdrop',
      direction: this._dir ? this._dir.value : 'ltr',
      scrollStrategy: this._scrollStrategy()
    });

    this._popupRef = this._overlay.create(overlayState);
  }
/**
 * Create the popup PositionStrategy.
 * @return {?}
 */
private _createPopupPositionStrategy(): PositionStrategy {
    return this._overlay.position()
      .connectedTo(this._datepickerInput.getPopupConnectionElementRef(),
        {originX: 'start', originY: 'bottom'},
        {overlayX: 'start', overlayY: 'top'}
      )
      .withFallbackPosition(
        { originX: 'start', originY: 'top' },
        { overlayX: 'start', overlayY: 'bottom' }
      )
      .withFallbackPosition(
        {originX: 'end', originY: 'bottom'},
        {overlayX: 'end', overlayY: 'top'}
      )
      .withFallbackPosition(
        { originX: 'end', originY: 'top' },
        { overlayX: 'end', overlayY: 'bottom' }
      );
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-datepicker, mat-datepicker',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdDialog, },
{type: Overlay, },
{type: NgZone, },
{type: ViewContainerRef, },
{type: undefined, decorators: [{ type: Inject, args: [MD_DATEPICKER_SCROLL_STRATEGY, ] }, ]},
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: Directionality, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'startAt': [{ type: Input },],
'startView': [{ type: Input },],
'touchUi': [{ type: Input },],
'disabled': [{ type: Input },],
'selectedChanged': [{ type: Output },],
};
}

function MdDatepicker_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepicker.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepicker.ctorParameters;
/** @type {?} */
MdDatepicker.propDecorators;
/** @type {?} */
MdDatepicker.prototype._startAt;
/**
 * The view that the calendar should start in.
 * @type {?}
 */
MdDatepicker.prototype.startView;
/**
 * Whether the calendar UI is in touch mode. In touch mode the calendar opens in a dialog rather
 * than a popup and elements have more padding to allow for bigger touch targets.
 * @type {?}
 */
MdDatepicker.prototype.touchUi;
/** @type {?} */
MdDatepicker.prototype._disabled;
/**
 * Emits new selected date when selected date changes.
 * @deprecated Switch to the `dateChange` and `dateInput` binding on the input element.
 * @type {?}
 */
MdDatepicker.prototype.selectedChanged;
/**
 * Whether the calendar is open.
 * @type {?}
 */
MdDatepicker.prototype.opened;
/**
 * The id for the datepicker calendar.
 * @type {?}
 */
MdDatepicker.prototype.id;
/** @type {?} */
MdDatepicker.prototype._validSelected;
/**
 * A reference to the overlay when the calendar is opened as a popup.
 * @type {?}
 */
MdDatepicker.prototype._popupRef;
/**
 * A reference to the dialog when the calendar is opened as a dialog.
 * @type {?}
 */
MdDatepicker.prototype._dialogRef;
/**
 * A portal containing the calendar for this datepicker.
 * @type {?}
 */
MdDatepicker.prototype._calendarPortal;
/**
 * The element that was focused before the datepicker was opened.
 * @type {?}
 */
MdDatepicker.prototype._focusedElementBeforeOpen;
/** @type {?} */
MdDatepicker.prototype._inputSubscription;
/**
 * The input element this datepicker is associated with.
 * @type {?}
 */
MdDatepicker.prototype._datepickerInput;
/**
 * Emits when the datepicker is disabled.
 * @type {?}
 */
MdDatepicker.prototype._disabledChange;
/** @type {?} */
MdDatepicker.prototype._dialog;
/** @type {?} */
MdDatepicker.prototype._overlay;
/** @type {?} */
MdDatepicker.prototype._ngZone;
/** @type {?} */
MdDatepicker.prototype._viewContainerRef;
/** @type {?} */
MdDatepicker.prototype._scrollStrategy;
/** @type {?} */
MdDatepicker.prototype._dateAdapter;
/** @type {?} */
MdDatepicker.prototype._dir;
/** @type {?} */
MdDatepicker.prototype._document;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
